import React from 'react';
import { Inputs } from '../utils/roiCalculations';
import { managerView } from '../utils/commissions';
import { fmtMoney } from '../utils/formatters';
import { roiMultiple } from '../utils/roiMultiple';
import { YearOneHeadline } from '../utils/yearOneHeadline';
interface SummerPestHeadlineProps {
  headline: YearOneHeadline;
  inputs: Inputs;
  timeframe: number;
  grossProfit: number;
  contribution: number;
  roi: number;
  scenarioActive: boolean;
}

// Headline cards in the language summer pest operators use: reps signed vs
// reps who make it to day 90, year-one accounts, and the new revenue those
// accounts actually bring in. Profit and return stay on the timeframe shown.
export function SummerPestHeadline({
  headline,
  inputs,
  timeframe,
  grossProfit,
  contribution,
  roi,
  scenarioActive
}: SummerPestHeadlineProps) {
  const manager = managerView(inputs);
  const day90Share = headline.signed > 0 ? headline.day90 / headline.signed : 0;
  const attritionNote = inputs.revenueCadence === 'monthly' && inputs.residualTicket ? `${inputs.monthlyAttrition || 2}%/mo attrition` : inputs.revenueCadence === 'quarterly' && inputs.residualTicket ? `${inputs.quarterlyAttrition || 5}%/qtr attrition` : 'no residual';
  const scenarioTag = scenarioActive ? <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-1696" data-mp="ifj8jui72dtw">(Scenario)</span> : null;
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6" data-id="element-1697" data-mp="dgg4aca1gbmz">
      <Card label={<>Reps Signed → Day 90{scenarioTag}</>} value={<span className="text-navy" data-id="element-1699" data-mp="3hqw5b7y25jx">
            {Math.round(headline.signed)}
            <span className="mx-1.5 text-gray-400 font-semibold" data-id="element-1700" data-mp="3jtk7ppjdom0">→</span>
            {headline.day90.toFixed(1)}
          </span>} note={`${Math.round(day90Share * 100)}% of year-one signs reach day 90 by Month 12`} data-id="element-1698" data-mp="8h3i7tqzr8lg" />
      <Card label={<>Year-One Accounts{scenarioTag}</>} value={<span className="text-navy" data-id="element-1702" data-mp="3nd5g309oa8pg">
            {Math.round(headline.accounts).toLocaleString()}
          </span>} note={`New accounts sold Months 1–12, after ${inputs.pullthrough}% pull-through`} data-id="element-1701" data-mp="1ttn2x1sp23zh" />
      <Card label={<>New Revenue, Year-One Accounts{scenarioTag}</>} value={<span className="text-navy" data-id="element-1704" data-mp="3id0yf6x137xn">{fmtMoney(headline.accountRevenue)}</span>} note={`${fmtMoney(headline.revenuePerAccount)} per account over its first 12 months, after ${attritionNote}`} data-id="element-1703" data-mp="3ed09utg7tgcv" />
      <Card label={<>
            {timeframe}-Month {manager ? 'Overrides' : 'Gross Profit'}
            {scenarioTag}
          </>} value={<span className="text-green-600" data-id="element-1706" data-mp="5w5gc4lb58bg">{fmtMoney(grossProfit)}</span>} note={manager ? 'Upfront + backend overrides, net of clawbacks' : 'Margin on the revenue above'} data-id="element-1705" data-mp="ew0xcq1jnxje" />
      <Card label={<>
            {timeframe}-Month Return on Spend
            {scenarioTag}
          </>} value={<span className={roi >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-1708" data-mp="2e40vbgo21q3u">
            {roiMultiple(roi)}
          </span>} note={`Contribution after recruiting costs: ${fmtMoney(contribution)}`} data-id="element-1707" data-mp="2qnnzvrd2d49i" />
    </div>;
}
function Card({
  label,
  value,
  note
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  note: string;
}) {
  return <div className="flex flex-col p-4 md:p-5 rounded-xl bg-gray-50 border border-gray-200" data-id="element-1709" data-mp="1zfnua5a41h2h">
      <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-1710" data-mp="1uhcp47urfuql">{label}</div>
      <div className="text-2xl md:text-[1.75rem] font-bold leading-tight tabular-nums" data-id="element-1711" data-mp="2957d5axtpaxc">
        {value}
      </div>
      <div className="text-xs text-gray-600 mt-2" data-id="element-1712" data-mp="24dfmz1ikx77q">{note}</div>
    </div>;
}